import {html, LitElement} from 'lit';
import styles from './x-fans-like-card.component.scss?as=stylesheet';
import {ref, createRef} from 'lit/directives/ref.js';
import {classMap} from 'lit/directives/class-map.js';
import 'custom-extensions/g-modal';
import GTM from 'extensions/gtm';

const NO_IMAGE_THUMBNAIL = `${window.mivaJS.Image_Placeholder_Product_Empty}`;

export class FansLikeCard extends LitElement {

	imageRef = createRef();

	buttonRef = createRef();

	static styles = [styles];

	static get properties () {
		return {
			buttonDisabled: {
				state: false,
				type: Boolean
			},
			code: {
				type: String
			},
			gtmCategory: {
				attribute: 'gtm-category',
				type: String
			},
			gtmListId: {
				attribute: 'gtm-list-id',
				type: String
			},
			gtmListName: {
				attribute: 'gtm-list',
				type: String
			},
			gtmPosition: {
				attribute: 'gtm-position',
				type: Number
			},
			imgSrcDesktop: {
				attribute: 'image-src-desktop',
				type: String
			},
			imgSrcMobile: {
				attribute: 'image-src-mobile',
				type: String
			},
			originalPrice: {
				attribute: 'original-price',
				type: Number
			},
			name: {
				type: String
			},
			price: {
				type: Number
			},
			productId: {
				attribute: 'product-id',
				type: String
			},
			styleNumber: {
				attribute: 'style-number',
				type: String
			},
			sourceLoaded: {
				state: true,
				type: Boolean
			},
			url: {
				type: String
			}
		};
	}

	constructor () {
		super();

		// Create gtm helper
		this.gtm = new GTM(window.mivaJS);

		this.code = '';
		this.formattedBasePrice = '';
		this.formattedPrice = '';
		this.gtmCategory = '';
		this.gtmListId = '';
		this.gtmListName = '';
		this.gtmPosition = 0;
		this.imgHeight = 55;
		this.imgSrcDesktop = '';
		this.imgSrcMobile = '';
		this.imgWidth = 40;
		this.name = '';
		this.noImageSrc = NO_IMAGE_THUMBNAIL;
		this.originalPrice = 0;
		this.price = 0;
		this.productId = '';
		this.styleNumber = '';
		this.sourceLoaded = false;
		this.buttonDisabled = false;
		this.url = '';
	}

	connectedCallback() {
		super.connectedCallback();
	}

	handleSourceLoad () {
		this.sourceLoaded = true;
	}

	handleClick (e) {
		const product = {
			brand: this.brand,
			id: this.styleNumber,
			/* eslint-disable camelcase */
			item_brand: this.brand,
			item_id: this.styleNumber,
			item_list_id: this.gtmListId,
			item_list_name: this.gtmListName,
			item_name: this.name,
			/* eslint-enable camelcase */
			list: this.gtmListName,
			name: this.name,
			position: this.gtmPosition,
			price: this.price
		};

		this.gtm.searchSendProductClick(product);

		// adjust product data for Searchspring
		product.id = this.productId;
		const ssFields = {
			sku: this.code,
			uid: this.productId,
			url: this.url
		};

		let prodClickEvent = new CustomEvent('fans-also-like-card-click', {
			bubbles: true,
			cancelable: true,
			composed: true,
			detail: {
				...product,
				...ssFields
			}
		});
		this.dispatchEvent(prodClickEvent);
		if (prodClickEvent.defaultPrevented) {
			e.preventDefault();
		}
	}

	renderPrice () {
		let strikethroughPriceHtml = '';

		// Price formatter
		const usCurrency = new Intl.NumberFormat('en-US', {
			currency: 'USD',
			style: 'currency'
		});

		if (this.price < this.originalPrice) {
			strikethroughPriceHtml = html`<span class="x-product-fans-also-liked-product__price u-text-strike u-color-gray-4">${usCurrency.format(this.originalPrice)}</span>`;
		}

		return html`
			<div class="x-product-fans-also-liked__pricing">
				${strikethroughPriceHtml}
				<span class="x-product-fans-also-liked-product__price">${usCurrency.format(this.price)}</span>
			</div>
		`;
	}

	renderImage () {
		return html`
		<picture>
			<source data-srcset="${this.htmlEntities(this.imgSrcMobile || this.noImageSrc)}" media="(max-width: 767px)">
			<img ${ref(this.imageRef)} class="lazyload" src="${this.imgSrcDesktop || this.noImageSrc}" height="${this.imgHeight}" width="${this.imgWidth}" data-src="${this.htmlEntities(this.imgSrcDesktop)}" alt="${this.htmlEntities(this.name)} image" />
		</picture>
		`;
	}

	render () {
		const classes = {
			'image-loaded': this.sourceLoaded
		};

		return html`
			<div id="js-product-fans-also-liked-${this.code}" class="${classMap(classes)} x-product-fans-also-liked x-layout x-layout__item" @click="${this.handleClick}">
				<div class="x-product-fans-also-liked__image x-layout__item">
					<a href="${this.htmlEntities(this.url)}">
						${this.renderImage()}
					</a>
				</div>
				<div class="x-product-fans-also-liked__details x-layout__item">
					<div class="x-product-fans-also-liked__name">
						<a href="${this.htmlEntities(this.url)}" class="x-product-fans-also-liked__link">${this.htmlEntities(this.name)}</a>
					</div>
					${this.renderPrice()}
				</div>
				<div class="x-product-fans-also-liked__cta x-layout__item">
					<g-modal type="iframe" target="${this.quickAddUrl()}" id="js-quick-add-${this.code}" class="g-modal-quick-add-iframe" title="true">
						<header slot="title">Fans Also Liked</header>
						<button slot="trigger" type="button" class="g-button g-button-secondary x-product-fans-also-liked__button-add" aria-label="Add Product" ?disabled=${this.buttonDisabled} ${ref(this.buttonRef)}>+ Add</button>
					</g-modal>
				</div>
			</div>
		`;
	}

	// Helpers
	quickAddUrl () {
		let quickAddParam = '?QuickAdd=1';
		if (this.url.includes('?')) {
			quickAddParam = '&QuickAdd=1';
		}
		return `${this.htmlEntities(this.url + quickAddParam)}`;
	}

	htmlEntities (str) {
		// eslint-disable-next-line newline-per-chained-call
		return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
	}
};

window.customElements.define('x-fans-like-card', FansLikeCard);
